import { baseApi } from '@/shared/api/baseApi.js';
import type { CallType } from '@prostostroy/shared';

export interface CallTokenResponse {
  callId: string;
  livekitUrl: string;
  token: string;
}

export interface StartCallArgs {
  calleeId: string;
  type: CallType;
  conversationId?: string | null;
}

export interface CallHistoryItem {
  id: string;
  conversationId: string | null;
  peer: { id: string; name: string; avatarUrl: string | null } | null;
  type: CallType;
  status: string;
  isOutgoing: boolean;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  endReason: string | null;
}

export const callsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    startCall: b.mutation<CallTokenResponse, StartCallArgs>({
      query: (body) => ({ url: '/calls/start', method: 'POST', body }),
      transformResponse: (r: { success: boolean; data: CallTokenResponse }) => r.data,
      invalidatesTags: ['CallHistory'],
    }),
    acceptCall: b.mutation<CallTokenResponse, string>({
      query: (callId) => ({ url: `/calls/${callId}/accept`, method: 'POST' }),
      transformResponse: (r: { success: boolean; data: CallTokenResponse }) => r.data,
      invalidatesTags: ['CallHistory'],
    }),
    rejectCall: b.mutation<void, { callId: string; reason?: string }>({
      query: ({ callId, reason }) => ({
        url: `/calls/${callId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['CallHistory'],
    }),
    endCall: b.mutation<void, { callId: string; reason?: string }>({
      query: ({ callId, reason }) => ({
        url: `/calls/${callId}/end`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['CallHistory'],
    }),
    getCallHistory: b.query<CallHistoryItem[], void>({
      query: () => ({ url: '/calls/history' }),
      transformResponse: (r: { success: boolean; data: CallHistoryItem[] }) => r.data,
      providesTags: ['CallHistory'],
    }),
  }),
});

export const {
  useStartCallMutation,
  useAcceptCallMutation,
  useRejectCallMutation,
  useEndCallMutation,
  useGetCallHistoryQuery,
} = callsApi;
